import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Modal, Segmented, Skeleton, Tag, Typography, message as antMessage } from 'antd';
import type { ExportGrantAccount, ExportGrantPreview } from '@youkuai/contracts';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { confirmExportGrantChange, getExportGrantPreview, listExportGrantAccounts,
  previewExportGrantChange } from './export-grants-api.ts';
import { ApiRequestError } from './project-api.ts';

const { Text, Title } = Typography;

export function shouldLoadExportGrantPreview(input: { previewId: string | null; token: string;
  isAdmin: boolean; currentPreviewId: string | null; ignoredPreviewId: string | null }): boolean {
  return !!input.previewId && !!input.token && input.isAdmin && input.currentPreviewId !== input.previewId &&
    input.ignoredPreviewId !== input.previewId;
}

function issueText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号不是有效管理员，或权限已变化。',
      TARGET_NOT_FOUND: '目标账号已停用或不属于当前环境，请刷新。',
      STATE_CONFLICT: '授权状态已经是目标状态，请刷新列表。',
      PREVIEW_NOT_FOUND: '该预览不属于当前管理员，或已经不存在。',
      PREVIEW_EXPIRED: '影响预览已过期，请重新生成。',
      PREVIEW_CONSUMED: '该预览已经执行，不能重复确认。',
      PREVIEW_STALE: '账号、授权或未过期票据已变化，请重新生成影响预览。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '本次确认标识已用于其他操作，请重新发起。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

export function ExportGrantsPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const userId = state.kind === 'ready' ? state.session.userId : '';
  const isAdmin = state.kind === 'ready' && state.session.roles.includes('ADMIN');
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [category, setCategory] = useState<'CONTRACT_AMOUNT' | 'BANK_ACCOUNT'>('CONTRACT_AMOUNT');
  const [target, setTarget] = useState<ExportGrantAccount | null>(null);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<ExportGrantPreview | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const confirmation = useRef<{ previewId: string; key: string } | null>(null);
  const ignoredPreview = useRef<string | null>(null);
  const accounts = useInfiniteQuery({ queryKey: ['export-grant-accounts', userId, category, activeSearch],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listExportGrantAccounts(token, { search: activeSearch, category,
      ...(pageParam ? { afterCode: pageParam } : {}) }),
    getNextPageParam: (page) => page.nextCursor ?? undefined, enabled: !!token && isAdmin });
  const rows = accounts.data?.pages.flatMap((page) => page.items) ?? [];
  const previewMutation = useMutation({ mutationFn: (input: { targetUserId: string; action: 'GRANT' | 'REVOKE';
    category: 'CONTRACT_AMOUNT' | 'BANK_ACCOUNT'; reason: string }) => previewExportGrantChange(token, input) });
  const confirmMutation = useMutation({ mutationFn: (input: { previewId: string; idempotencyKey: string }) =>
    confirmExportGrantChange(token, input) });

  useEffect(() => {
    const previewId = params.get('preview');
    if (!previewId) { ignoredPreview.current = null; return; }
    if (!shouldLoadExportGrantPreview({ previewId, token, isAdmin,
      currentPreviewId: preview?.previewId ?? null, ignoredPreviewId: ignoredPreview.current })) return;
    let active = true;
    void getExportGrantPreview(token, previewId).then((value) => {
      if (!active) return;
      setCategory(value.category); setPreview(value); setTarget(value.target); setReason(value.reason); setNotice(null);
    }).catch((error: unknown) => { if (active) setNotice({ type: 'error', text: issueText(error) }); });
    return () => { active = false; };
  }, [isAdmin, params, preview?.previewId, token]);

  function open(row: ExportGrantAccount) {
    setTarget(row); setReason(''); setPreview(null); setNotice(null); confirmation.current = null;
    setParams({}, { replace: true });
  }

  function close() {
    if (previewMutation.isPending || confirmMutation.isPending) return;
    setTarget(null); setReason(''); setPreview(null); confirmation.current = null;
    setParams({}, { replace: true });
  }

  async function buildPreview() {
    if (!target || reason.trim().length < 10) return;
    try {
      const result = await previewMutation.mutateAsync({ targetUserId: target.userId,
        category, action: target.granted ? 'REVOKE' : 'GRANT', reason: reason.trim() });
      ignoredPreview.current = null;
      setPreview(result); setParams({ preview: result.previewId }, { replace: true }); setNotice(null);
    } catch (error) { setNotice({ type: 'error', text: issueText(error) }); }
  }

  async function confirm() {
    if (!preview) return;
    if (confirmation.current?.previewId !== preview.previewId) {
      confirmation.current = { previewId: preview.previewId, key: `export-grant-${crypto.randomUUID()}` };
    }
    try {
      const result = await confirmMutation.mutateAsync({ previewId: preview.previewId,
        idempotencyKey: confirmation.current.key });
      ignoredPreview.current = preview.previewId;
      setTarget(null); setPreview(null); setReason(''); confirmation.current = null;
      setParams({}, { replace: true });
      await client.invalidateQueries({ queryKey: ['export-grant-accounts', userId, category] });
      const categoryName = category === 'CONTRACT_AMOUNT' ? '合同金额' : '银行账户';
      setNotice({ type: 'success', text: result.granted
        ? `${categoryName}导出权已授予；导出时仍会逐次核验页面数据权。`
        : `${categoryName}导出权已撤销；旧导出票据已立即失效。` });
    } catch (error) {
      const text = issueText(error); setNotice({ type: 'error', text }); void antMessage.error(text); throw error;
    }
  }

  return <main className="page-shell customer-shell">
    <Link className="back-link" to="/projects">← 返回我的项目</Link>
    <div className="section-kicker">SECURITY / 独立授权</div>
    <div className="project-heading"><div><Title level={1}>敏感数据导出权限</Title>
      <p>页面查看权不自动包含导出权；合同金额与银行账户分别授权。</p></div></div>
    {!isAdmin ? <Alert type="error" showIcon title="当前账号无权限管理导出授权" /> : <>
      {notice && <Alert className="workspace-alert" showIcon type={notice.type} title={notice.text} closable
        onClose={() => setNotice(null)} />}
      <Segmented className="workspace-segmented" value={category} options={[
        { label: '合同金额', value: 'CONTRACT_AMOUNT' }, { label: '银行账户', value: 'BANK_ACCOUNT' },
      ]} onChange={(value) => { if (value === 'CONTRACT_AMOUNT' || value === 'BANK_ACCOUNT') setCategory(value);
        setTarget(null); setPreview(null); setParams({}, { replace: true }); }} />
      <Input.Search value={search} onChange={(event) => setSearch(event.target.value)}
        onSearch={() => setActiveSearch(search)} allowClear placeholder="按人员编号或姓名查找"
        enterButton="查找账号" className="project-search" />
      {accounts.isPending && <Skeleton active paragraph={{ rows: 5 }} />}
      {accounts.isError && <Alert type="error" showIcon title="账号授权状态读取失败"
        description={issueText(accounts.error)} action={<Button onClick={() => void accounts.refetch()}>重试</Button>} />}
      {!accounts.isPending && !accounts.isError && rows.length === 0 && <Empty description="没有匹配的活跃内部账号" />}
      <div className="customer-rows">{rows.map((row) => <article className="customer-row" key={row.userId}>
        <div><span className="customer-code">{row.personCode} · {row.engagementType === 'EMPLOYEE' ? '全职员工' : '长期合作执行人'}</span>
          <h3>{row.displayName}</h3><p>{category === 'CONTRACT_AMOUNT'
            ? '合同金额导出权与项目查看权分别校验'
            : '银行账户导出权与明文查看权分别校验'}</p>
          <Tag color={row.granted ? 'success' : 'default'}>{row.granted ? '已独立授权' : '未授权'}</Tag>
          {row.grantEffectiveAt && <Text type="secondary"> · {new Date(row.grantEffectiveAt).toLocaleString('zh-CN')} 生效</Text>}</div>
        <Button danger={row.granted} onClick={() => open(row)}>{row.granted ? '预览撤销影响' : '预览授予影响'}</Button>
      </article>)}</div>
      {accounts.hasNextPage && <Button loading={accounts.isFetchingNextPage} onClick={() => void accounts.fetchNextPage()}>
        {accounts.isFetchNextPageError ? '重试加载' : '继续加载账号'}</Button>}
      <Modal title={`${target?.granted ? '撤销' : '授予'}${category === 'CONTRACT_AMOUNT' ? '合同金额' : '银行账户'}导出权`} open={!!target} footer={null}
        destroyOnHidden onCancel={close}>
        <p>目标账号：{target?.personCode} · {target?.displayName}</p>
        {!preview ? <>
          <Alert type="info" showIcon title="先生成影响预览" description="预览有效 5 分钟，并绑定当前管理员身份；这一步不会变更权限。" />
          <p>填写本次权限变更依据（至少 10 字）：</p>
          <Input.TextArea value={reason} onChange={(event) => setReason(event.target.value)} rows={3}
            maxLength={500} placeholder="例如：项目负责人需要核对本人负责项目合同金额" />
          <div className="customer-modal-actions"><Button onClick={close}>取消</Button>
            <Button type="primary" loading={previewMutation.isPending} disabled={reason.trim().length < 10}
              onClick={() => void buildPreview()}>生成影响预览</Button></div>
        </> : <>
          <Alert type={preview.action === 'REVOKE' ? 'warning' : 'info'} showIcon
            title={`影响 ${preview.impact.users} 个账号、${preview.impact.permissions} 项权限`}
            description={preview.action === 'REVOKE'
              ? `当前有 ${preview.impact.activeTickets} 张未过期票据；确认撤销后全部立即不能下载。金额影响未知，不伪造金额。`
              : `仅授予${category === 'CONTRACT_AMOUNT' ? '合同金额' : '银行账户'}导出类别；不会自动获得数据查看权。金额影响未知，不伪造金额。`} />
          <p>变更依据：{preview.reason}</p><p>预览摘要：<Text code>{preview.sourceDigest.slice(0, 16)}…</Text></p>
          <p>有效至：{new Date(preview.expiresAt).toLocaleString('zh-CN')}</p>
          <div className="customer-modal-actions"><Button onClick={() => { setPreview(null); setParams({}, { replace: true }); }}>重新填写</Button>
            <Button type="primary" danger={preview.action === 'REVOKE'} loading={confirmMutation.isPending}
              onClick={() => Modal.confirm({ title: `二次确认：${preview.action === 'GRANT' ? '授予' : '撤销'} 1 项导出权限？`,
                content: `影响 1 个账号、1 项权限；未过期票据 ${preview.impact.activeTickets} 张；金额影响未知。此次确认不增加审批人。`,
                okText: preview.action === 'GRANT' ? '确认授予' : '确认撤销',
                okButtonProps: { danger: preview.action === 'REVOKE' }, onOk: confirm })}>核对后继续</Button></div>
        </>}
      </Modal>
    </>}
  </main>;
}
